// Shipment.status → antd Tag color 對照（單一事實來源）
//
// 對應 backend `app/models/shipment.rb` 的 status enum（9 個值）。
// 任何頁面要顯示 shipment status tag 都從這裡 import，避免兩處 map 漂移
// （參考過往：OrderShow / ShipmentsList 對同 status 用不同色，operator 困惑）。
//
// 採用 antd 內建 status 語意色（processing/success/error/warning/default），跨頁面視覺一致：
// - 配送中三態（shipped / in_transit / arrived_at_hub）用 processing，「正在處理 / 移動中」
// - arrived_at_destination / received 用 success，「到達 / 完成」
// - returned / problem 用 error，「異常 / 退貨」
// - returned_to_sender 用 warning，「實體已回，需操作」（與 returned 區分）
// - waiting 用 default，剛建立還沒動
// 對應 backend `app/models/shipment.rb#enum :status` 的 9 個值。
export type ShipmentStatus =
  | 'waiting'
  | 'shipped'
  | 'in_transit'
  | 'arrived_at_hub'
  | 'arrived_at_destination'
  | 'received'
  | 'returned'
  | 'returned_to_sender'
  | 'problem'

// TS 編譯期保護的作用範圍 — 只在「本 const 定義處」生效，不延伸到 caller：
// - ✅ 在 _STRICT 增 ShipmentStatus union 但漏加 entry → TS 擋下
// - ✅ 在 _STRICT 加 typo key（不在 union 內）→ TS 擋下
// - ❌ caller 端拿 `status: string`（API 來）index → 走 export 的 `Record<string, string>`，TS **不會** 擋未知 status
//   未知 status 的 runtime 保護由 caller 的 `... || 'default'` fallback 處理（不是 type 系統）
//
// 想把保護延伸到 caller，需在 API boundary 把 `string` 顯式 narrow 成 `ShipmentStatus`
// （例如：API response zod / typia schema 驗證），不是這個 module 的責任。
const SHIPMENT_STATUS_COLORS_STRICT = {
  waiting: 'default',
  shipped: 'processing',
  in_transit: 'processing',
  arrived_at_hub: 'processing',
  arrived_at_destination: 'success',
  received: 'success',
  returned: 'error',
  returned_to_sender: 'warning',
  problem: 'error',
} satisfies Record<ShipmentStatus, string>

export const SHIPMENT_STATUS_COLORS: Record<string, string> = SHIPMENT_STATUS_COLORS_STRICT
